import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { listenForAuthChanges, logout } from "../../../redux/authSlice";
import type { RootState, AppDispatch } from "../../../redux/store";
import {
  selectMerchant, selectMerchantId, selectMode, selectIsLive,
  selectMerchantLoading, selectMerchantError, selectTransactions,
  selectBalance, selectStats, setMode, fetchUserMerchants,
 cleanupMerchantListeners,
  MerchantMode,
} from "../../../redux/merchantSlice";
import logo from "../../../assets/cosmoola/logo.jpg";
import {
  Home, BarChart3, ArrowLeftRight, Users, CreditCard, PieChart, ShieldAlert,
  Radar as RadarIcon, Monitor, Receipt, FileText, Repeat, Package,
  Grid3X3, Bell, Settings, Menu, X, ChevronDown, Search, LogOut, Key,
  Webhook, Palette, UserPlus, Shield,
} from "lucide-react";

import Balances from "./Balances";
import Transactions from "./Transactions";
import Customers from "./Customers";
import Analytics from "./Analytics";
import Disputes from "./Disputes";
import Radar from "./Radar";
import Terminal from "./Terminal";
import Billing from "./Billing";
import BillingOverview from "./BillingOverview";
import Subscriptions from "./Subscriptions";
import TestMode from "./TestMode";
import LiveMode from "./LiveMode";
import Webhooks from "./Webhooks";

/* ─── THEME (wallet.tsx) ─── */
const D = { bg:"#000000", surface:"#0a0a0a", surface2:"#101010", border:"#1c1c1c", border2:"#2a2a2a", white:"#ffffff", offwhite:"#e8e8e8", muted:"#6b6b6b", subtle:"#3a3a3a", gold:"#C9A96E" };
const L = { bg:"#ffffff", surface:"#fafafa", surface2:"#f3f3f3", border:"#e5e5e5", border2:"#d4d4d4", text:"#0a0a0a", textSoft:"#525252", muted:"#a3a3a3", subtle:"#d4d4d4" };
const GOLD_GRAD = "linear-gradient(135deg, #E8C96A, #C9A84C, #9A7A2E)";

type NavItem = { id:string; label:string; icon:any; children?:{ id:string; label:string; icon:any }[] };

const NAV: NavItem[] = [
  { id:"home", label:"Home", icon:Home },
  { id:"balances", label:"Balances", icon:BarChart3 },
  { id:"transactions", label:"Transactions", icon:ArrowLeftRight },
  { id:"customers", label:"Customers", icon:Users },
  { id:"payments", label:"Payments", icon:CreditCard, children:[
    { id:"analytics", label:"Analytics", icon:PieChart },
    { id:"disputes", label:"Disputes", icon:ShieldAlert },
    { id:"radar", label:"Radar", icon:RadarIcon },
    { id:"terminal", label:"Terminal", icon:Monitor },
  ]},
  { id:"billing", label:"Billing", icon:Receipt, children:[
    { id:"billing-overview", label:"Overview", icon:FileText },
    { id:"billing-manage", label:"Billing", icon:Receipt },
    { id:"subscriptions", label:"Subscriptions", icon:Repeat },
  ]},
  { id:"products", label:"Products", icon:Package },
    { id:"webhooks", label:"Webhooks", icon:Package },

];

const fmt = (n:number, c="eur") => { try { return new Intl.NumberFormat("en-US",{style:"currency",currency:c.toUpperCase(),minimumFractionDigits:2}).format(n); } catch { return `${c.toUpperCase()} ${n.toFixed(2)}`; } };
const timeAgo = (ts:number) => { if(!ts) return ""; const d=Date.now()-(ts<1e12?ts*1000:ts); if(d<60000) return "Now"; if(d<3.6e6) return `${Math.floor(d/60000)}m`; if(d<8.64e7) return `${Math.floor(d/3.6e6)}h`; return `${Math.floor(d/8.64e7)}d`; };
const greeting = () => { const h=new Date().getHours(); return h<12?"Good morning":h<17?"Good afternoon":"Good evening"; };
const titles:Record<string,string> = { home:"Dashboard",balances:"Balances",transactions:"Transactions",customers:"Customers",analytics:"Analytics",disputes:"Disputes",radar:"Radar",terminal:"Terminal","billing-manage":"Billing","billing-overview":"Billing Overview",subscriptions:"Subscriptions",products:"Products" };

export default function MerchantDashboard() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams<{userId?:string}>();
  const location = useLocation();

  const authUser = useSelector((s:RootState) => s.auth.user);
const authLoading = useSelector((s: RootState) => s.auth.status === "loading");  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);

  const rMid = useSelector(selectMerchantId);
  const userId = params.userId || (location.state as any)?.userId || rMid || authUser?.userId || null;

  const merchant = useSelector(selectMerchant);
  const mode = useSelector(selectMode);
  const isLive = useSelector(selectIsLive);
  const mLoading = useSelector(selectMerchantLoading);
  const mError = useSelector(selectMerchantError);
  const transactions = useSelector(selectTransactions);
  const balance = useSelector(selectBalance);
  const stats = useSelector(selectStats);



  const [view, setView] = useState("home");
  const [sidebar, setSidebar] = useState(false);
  const [expanded, setExpanded] = useState<string|null>(null);
  const [notif, setNotif] = useState(false);
  const [grid, setGrid] = useState(false);
  const [settings, setSettings] = useState(false);
  const [mobile, setMobile] = useState(window.innerWidth < 1024);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => { const fn = () => setMobile(window.innerWidth<1024); window.addEventListener("resize",fn); return () => window.removeEventListener("resize",fn); }, []);
  useEffect(() => { const fn = (e:MouseEvent) => { if(popRef.current && !popRef.current.contains(e.target as Node)){setNotif(false);setGrid(false);setSettings(false);} }; document.addEventListener("mousedown",fn); return () => document.removeEventListener("mousedown",fn); }, []);


useEffect(() => {
  if (!authLoading && authUser) {
    const id = params.userId || rMid || authUser.userId;
    if (id) {
      dispatch(fetchUserMerchants(id));
    }
  }
}, [authLoading, authUser, params.userId, rMid, dispatch]);

  const navClick = useCallback((item:NavItem) => { if(item.children) setExpanded(p=>p===item.id?null:item.id); else { setView(item.id); if(mobile) setSidebar(false); } }, [mobile]);
  const childClick = useCallback((id:string) => { setView(id); if(mobile) setSidebar(false); }, [mobile]);

  const renderPage = () => {
    switch(view) {
       case "api-keys": 
      return isLive ? <LiveMode /> : <TestMode />;
      case "balances": return <Balances />;
      case "transactions": return <Transactions />;
      case "customers": return <Customers />;
      case "analytics": return <Analytics />;
      case "disputes": return <Disputes />;
      case "radar": return <Radar />;
      case "terminal": return <Terminal />;
      case "billing-manage": return <Billing />;
      case "billing-overview": return <BillingOverview />;
      case "subscriptions": return <Subscriptions />;
      case "products": return <ProductsPage />;
      case "webhooks": return <Webhooks />;

      default: return <HomePage merchant={merchant} stats={stats} balance={balance} transactions={transactions} mode={mode} />;
    }
  };

  if (authLoading || (mLoading && !merchant)) return (
    <div style={{ minHeight:"100vh", background:L.bg, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Montserrat',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{ textAlign:"center" }}>
        <div className="md-spin" style={{ width:36, height:36, border:`2px solid ${L.border}`, borderTopColor:D.gold, borderRadius:"50%", margin:"0 auto 20px" }} />
        <div style={{ fontSize:11, color:L.muted, letterSpacing:"0.1em" }}>Loading dashboard…</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight:"100vh", background:L.bg, fontFamily:"'Montserrat','Cormorant Garamond',system-ui,sans-serif", color:L.text, display:"flex" }}>
      <style>{CSS}</style>

      {/* overlay */}
      <AnimatePresence>
        {mobile && sidebar && <motion.div key="ov" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setSidebar(false)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.25)", zIndex:998, backdropFilter:"blur(3px)" }} />}
      </AnimatePresence>

      {/* ═══ SIDEBAR ═══ */}
      <aside className="md-sidebar" style={{ transform: mobile && !sidebar ? "translateX(-100%)" : "translateX(0)", boxShadow: mobile && sidebar ? "6px 0 24px rgba(0,0,0,0.06)" : "none" }}>
        <div style={{ padding:"20px 20px 16px", borderBottom:`1px solid ${L.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={()=>setView("home")}>
            <img src={logo} alt="C" style={{ width:24, height:24, objectFit:"cover" }} />
            <span style={{ fontSize:13, fontWeight:700, letterSpacing:"0.08em" }}>COSMOOLA</span>
          </div>
          {mobile && <button onClick={()=>setSidebar(false)} className="md-icon-btn"><X size={18} color={L.textSoft} /></button>}
        </div>

        {/* mode */}
    <div style={{ padding:"12px 20px 8px" }}>
  <button 
    onClick={() => {
      const nextMode = isLive ? "test" : "live";
      dispatch(setMode(nextMode));
      // Force the view to refresh to the correct file
      setView("api-keys"); 
    }} 
    style={{ 
      width:"100%", 
      padding:"9px 14px", 
      background: isLive ? `rgba(45,139,95,0.06)` : `rgba(201,169,110,0.06)`, 
      border: `1px solid ${isLive ? "rgba(45,139,95,0.2)" : "rgba(201,169,110,0.2)"}`, 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "space-between", 
      cursor: "pointer", 
      transition: "all .2s" 
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: isLive ? "#2D8B5F" : D.gold }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: isLive ? "#2D8B5F" : "#9A7A2E", textTransform: "uppercase" }}>
        {isLive ? "Live" : "Test"} Mode
      </span>
    </div>
    <span style={{ fontSize: 9, color: L.muted, fontWeight: 600, letterSpacing: "0.05em" }}>SWITCH</span>
  </button>
</div>


        {/* search */}
        <div style={{ padding:"4px 20px 14px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", background:L.surface, border:`1px solid ${L.border}`, cursor:"text" }}>
            <Search size={14} color={L.muted} />
            <span style={{ fontSize:11, color:L.muted }}>Search…</span>
            <span style={{ marginLeft:"auto", fontSize:9, color:L.subtle, background:L.bg, padding:"1px 5px", border:`1px solid ${L.border}` }}>⌘K</span>
          </div>
        </div>

        {/* nav */}
        <nav style={{ flex:1, overflowY:"auto", padding:"0 10px" }}>
          {NAV.map(item => {
            const active = view===item.id || item.children?.some(c=>c.id===view);
            const exp = expanded===item.id;
            const Icon = item.icon;
            return (
              <div key={item.id}>
                <button onClick={()=>navClick(item)} className="md-nav-btn" style={{ background:active?L.surface:"transparent", color:active?L.text:L.textSoft }}>
                  <Icon size={16} strokeWidth={1.5} style={{ opacity:active?1:0.5 }} />
                  <span style={{ fontSize:12, fontWeight:active?600:400, flex:1, textAlign:"left" }}>{item.label}</span>
                  {item.children && <ChevronDown size={12} color={L.muted} style={{ transform:exp?"rotate(180deg)":"rotate(0)", transition:"transform .2s" }} />}
                </button>
                <AnimatePresence>
                  {item.children && exp && (
                    <motion.div initial={{height:0,opacity:0}} animate={{height:"auto",opacity:1}} exit={{height:0,opacity:0}} transition={{duration:0.2}} style={{ overflow:"hidden", paddingLeft:30 }}>
                      {item.children.map(ch => {
                        const ca = view===ch.id;
                        const ChIcon = ch.icon;
                        return (
                          <button key={ch.id} onClick={()=>childClick(ch.id)} className="md-nav-btn" style={{ background:ca?`rgba(201,169,110,0.06)`:"transparent", color:ca?"#9A7A2E":L.muted, padding:"7px 10px" }}>
                            <ChIcon size={13} strokeWidth={1.5} />
                            <span style={{ fontSize:11, fontWeight:ca?600:400 }}>{ch.label}</span>
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* footer */}
        <div style={{ padding:"14px 18px", borderTop:`1px solid ${L.border}` }}>
          <div style={{ display:"flex", alignItems:"center", gap:9 }}>
            <div style={{ width:28, height:28, background:GOLD_GRAD, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color:D.bg }}>{merchant?.businessName?.charAt(0)?.toUpperCase()||"C"}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{merchant?.businessName||"Your Business"}</div>
              <div style={{ fontSize:9, color:L.muted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{merchant?.email||""}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ═══ MAIN ═══ */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>

        {/* HEADER */}
        <header ref={popRef} style={{ height:58, background:"rgba(0,0,0,0.95)", backdropFilter:"blur(12px)", borderBottom:`1px solid ${D.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", position:"sticky", top:0, zIndex:100 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            {mobile && <button onClick={()=>setSidebar(true)} className="md-icon-btn"><Menu size={18} color={D.offwhite} /></button>}
            <h1 style={{ fontSize:13, fontWeight:600, color:D.white, letterSpacing:"0.04em" }}>{titles[view]||"Dashboard"}</h1>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:2 }}>
            {/* grid */}
            <div style={{ position:"relative" }}>
              <button onClick={()=>{setGrid(!grid);setNotif(false);setSettings(false)}} className="md-icon-btn" style={{ background:grid?"rgba(255,255,255,0.06)":"transparent" }}><Grid3X3 size={16} color={grid?D.white:D.muted} /></button>
              <AnimatePresence>
                {grid && (
                  <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:6}} className="md-dropdown" style={{ width:240 }}>
                    <div style={{ padding:"10px 14px 6px" }}><span style={{ fontSize:9, letterSpacing:"0.12em", color:D.muted, textTransform:"uppercase" }}>Quick Access</span></div>
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:2, padding:"0 6px 10px" }}>
                      {[{l:"Home",i:Home,id:"home"},{l:"Balance",i:BarChart3,id:"balances"},{l:"Payments",i:CreditCard,id:"analytics"},{l:"Billing",i:Receipt,id:"billing-overview"},{l:"Customers",i:Users,id:"customers"},{l:"Terminal",i:Monitor,id:"terminal"},{l:"Radar",i:RadarIcon,id:"radar"},{l:"Disputes",i:ShieldAlert,id:"disputes"},{l:"Docs",i:FileText,id:"docs"}].map(q => {
                        const QI = q.i;
                        return <button key={q.id} onClick={()=>{setView(q.id);setGrid(false)}} className="md-grid-btn"><QI size={16} color={D.muted} strokeWidth={1.5} /><span style={{ fontSize:9, color:D.muted }}>{q.l}</span></button>;
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {/* bell */}
            <div style={{ position:"relative" }}>
              <button onClick={()=>{setNotif(!notif);setGrid(false);setSettings(false)}} className="md-icon-btn" style={{ position:"relative", background:notif?"rgba(255,255,255,0.06)":"transparent" }}>
                <Bell size={16} color={notif?D.white:D.muted} />
                <div style={{ position:"absolute", top:7, right:7, width:5, height:5, borderRadius:"50%", background:"#C44D4D", border:`1.5px solid ${D.bg}` }} />
              </button>
              <AnimatePresence>
                {notif && (
                  <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:6}} className="md-dropdown" style={{ width:300 }}>
                    <div style={{ padding:"14px 18px", borderBottom:`1px solid ${D.border}`, display:"flex", justifyContent:"space-between" }}>
                      <span style={{ fontSize:12, fontWeight:600, color:D.white }}>Notifications</span>
                      <span style={{ fontSize:9, color:D.gold, cursor:"pointer" }}>Mark all read</span>
                    </div>
                    {[{t:"Payment received",d:"€1,200.00 from John Doe",time:"2m",c:"#2D8B5F"},{t:"Dispute opened",d:"Order #4829 — chargeback",time:"1h",c:"#C44D4D"},{t:"Payout completed",d:"€8,450.00 to your bank",time:"3h",c:"#4A7FC4"}].map((n,i) => (
                      <div key={i} style={{ padding:"12px 18px", borderBottom:`1px solid ${D.border}`, display:"flex", gap:10, cursor:"pointer", transition:"background .15s" }}
                        onMouseEnter={e=>(e.currentTarget.style.background=D.surface2)} onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
                        <div style={{ width:6, height:6, borderRadius:"50%", background:n.c, marginTop:5, flexShrink:0 }} />
                        <div>
                          <div style={{ fontSize:11, fontWeight:600, color:D.white }}>{n.t}</div>
                          <div style={{ fontSize:10, color:D.muted, marginTop:2 }}>{n.d}</div>
                          <div style={{ fontSize:9, color:D.subtle, marginTop:3 }}>{n.time} ago</div>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {/* settings */}
            <div style={{ position:"relative" }}>
              <button onClick={()=>{setSettings(!settings);setGrid(false);setNotif(false)}} className="md-icon-btn" style={{ background:settings?"rgba(255,255,255,0.06)":"transparent" }}><Settings size={16} color={settings?D.white:D.muted} /></button>
              <AnimatePresence>
                {settings && (
                  <motion.div initial={{opacity:0,y:6}} animate={{opacity:1,y:0}} exit={{opacity:0,y:6}} className="md-dropdown" style={{ width:200 }}>
                    {[{l:"Account",p:"/merchantaccountsettings",i:Settings},{l:"Team",p:"/team",i:UserPlus},{l:"API Keys",p:"/apikeys",i:Key},{l:"Webhooks",p:"/webhooks",i:Webhook},{l:"Branding",p:"/settings/branding",i:Palette}].map(s => {
                      const SI = s.i;
                      return <button key={s.l} onClick={()=>{navigate(s.p);setSettings(false)}} className="md-settings-btn"><SI size={14} color={D.muted} strokeWidth={1.5} />{s.l}</button>;
                    })}
                    <div style={{ borderTop:`1px solid ${D.border}`, margin:"3px 0" }} />
<button
  onClick={() => dispatch(logout())}
  className="md-settings-btn"
  style={{ color: "#C44D4D" }}
>
  <LogOut size={14} color="#C44D4D" strokeWidth={1.5} />Sign out
</button>

         </motion.div>
                )}
              </AnimatePresence>
            </div>
            {/* avatar */}
            <div style={{ width:28, height:28, background:GOLD_GRAD, display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:D.bg, marginLeft:6, cursor:"pointer" }}>
              {merchant?.businessName?.charAt(0)?.toUpperCase()||authUser?.email?.charAt(0)?.toUpperCase()||"U"}
            </div>
          </div>
        </header>

        {/* CONTENT */}
        <main style={{ flex:1, padding:mobile?"20px 16px":"28px 28px", overflowY:"auto" }}>
          {mError && <div style={{ padding:"12px 16px", background:"rgba(196,77,77,0.06)", border:"1px solid rgba(196,77,77,0.15)", marginBottom:16, fontSize:12, color:"#C44D4D", fontWeight:500 }}>{mError}</div>}
          <AnimatePresence mode="wait">
            <motion.div key={view} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-6}} transition={{duration:0.2}}>
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

/* ═══ HOME ═══ */
function HomePage({ merchant, stats, balance, transactions, mode }:{ merchant:any; stats:any; balance:any; transactions:any[]; mode:MerchantMode }) {
  return (
    <div>
      <div style={{ marginBottom:28 }}>
        <h2 style={{ fontSize:24, fontFamily:"'Cormorant Garamond',serif", fontWeight:400, color:L.text, marginBottom:4 }}>
          {greeting()}, <span style={{ fontStyle:"italic" }}>{merchant?.businessName||"there"}</span>
        </h2>
        <p style={{ fontSize:11, color:L.textSoft, letterSpacing:"0.04em" }}>Here's what's happening with your payments today.</p>
      </div>
      <div className="md-stat-grid">
        {[
          { l:"Total Volume", v:fmt(stats?.total_volume??0, merchant?.currency), ch:"+12.5%", up:true },
          { l:"Last 30 Days", v:fmt(stats?.last_30d_volume??0, merchant?.currency), ch:"+8.3%", up:true },
          { l:"Success Rate", v:`${(stats?.success_rate??0).toFixed(1)}%`, ch:"+2.1%", up:true },
          { l:"Disputes", v:`${stats?.pending??0}`, ch:"-3", up:false },
        ].map((s,i) => (
          <motion.div key={i} initial={{opacity:0,y:14}} animate={{opacity:1,y:0}} transition={{delay:i*0.06}}
            style={{ background:L.bg, border:`1px solid ${L.border}`, padding:"22px 20px", transition:"all .2s", cursor:"default" }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(201,169,110,0.3)";e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.03)"}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=L.border;e.currentTarget.style.boxShadow="none"}}>
            <div style={{ fontSize:9, fontWeight:600, letterSpacing:"0.12em", color:L.muted, marginBottom:8, textTransform:"uppercase" }}>{s.l}</div>
            <div style={{ fontSize:22, fontFamily:"'Cormorant Garamond',serif", fontWeight:600, color:L.text, marginBottom:4 }}>{s.v}</div>
            <div style={{ fontSize:10, fontWeight:600, color:s.up?"#2D8B5F":"#C44D4D" }}>{s.ch} <span style={{ color:L.muted, fontWeight:400 }}>vs last period</span></div>
          </motion.div>
        ))}
      </div>
      <div style={{ background:L.bg, border:`1px solid ${L.border}`, overflow:"hidden", marginTop:20 }}>
        <div style={{ padding:"16px 20px", borderBottom:`1px solid ${L.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div><div style={{ fontSize:13, fontWeight:600, color:L.text }}>Recent Transactions</div><div style={{ fontSize:10, color:L.muted, marginTop:2 }}>{mode==="live"?"Live":"Test"} mode</div></div>
          <span style={{ fontSize:10, color:D.gold, fontWeight:600, cursor:"pointer" }}>View all →</span>
        </div>
        {transactions.length===0?(
          <div style={{ padding:"40px 20px", textAlign:"center" }}><ArrowLeftRight size={28} color={L.subtle} strokeWidth={1} style={{ marginBottom:10 }} /><div style={{ fontSize:12, color:L.muted }}>No transactions yet.</div></div>
        ):(
          transactions.slice(0,6).map((tx,i) => (
            <div key={tx.id} style={{ padding:"12px 20px", borderBottom:i<5?`1px solid ${L.border}`:"none", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"background .15s", cursor:"pointer" }}
              onMouseEnter={e=>(e.currentTarget.style.background=L.surface)} onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <div style={{ width:6, height:6, borderRadius:"50%", background:tx.status==="succeeded"?"#2D8B5F":tx.status==="failed"?"#C44D4D":"#D4952B" }} />
                <div><div style={{ fontSize:12, fontWeight:500 }}>{tx.customer_email||tx.customer_name||tx.id.slice(0,16)}</div><div style={{ fontSize:10, color:L.muted }}>{tx.payment_method_types?.[0]||"card"} · {tx.status}</div></div>
              </div>
              <div style={{ textAlign:"right" }}><div style={{ fontSize:12, fontWeight:600 }}>{fmt(tx.amount/100,tx.currency)}</div><div style={{ fontSize:9, color:L.muted }}>{timeAgo(tx.created)}</div></div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ProductsPage() {
  return (
    <div>
      <h2 style={{ fontSize:22, fontFamily:"'Cormorant Garamond',serif", fontWeight:400, marginBottom:6 }}>Products</h2>
      <p style={{ fontSize:12, color:L.textSoft, marginBottom:28 }}>Manage your product catalog.</p>
      <div className="md-stat-grid">
        {[{n:"Payment Links",d:"Shareable payment links",i:"🔗"},{n:"Checkout",d:"Hosted checkout pages",i:"🛒"},{n:"Invoicing",d:"Professional invoices",i:"📄"},{n:"Connect",d:"Marketplace solutions",i:"🔄"}].map(p => (
          <div key={p.n} style={{ background:L.bg, border:`1px solid ${L.border}`, padding:24, cursor:"pointer", transition:"all .2s" }}
            onMouseEnter={e=>{e.currentTarget.style.borderColor="rgba(201,169,110,0.3)";e.currentTarget.style.transform="translateY(-1px)"}}
            onMouseLeave={e=>{e.currentTarget.style.borderColor=L.border;e.currentTarget.style.transform="translateY(0)"}}>
            <div style={{ fontSize:28, marginBottom:14 }}>{p.i}</div>
            <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>{p.n}</div>
            <div style={{ fontSize:11, color:L.textSoft, lineHeight:1.6 }}>{p.d}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
*,::before,::after{box-sizing:border-box;margin:0;padding:0}
body{background:#fff}button{font-family:inherit}
::selection{background:rgba(201,169,110,0.3);color:#0a0a0a}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#d4d4d4;border-radius:2px}
@keyframes spin{to{transform:rotate(360deg)}}
.md-spin{animation:spin .8s linear infinite}

.md-sidebar{width:248px;background:#fff;border-right:1px solid #e5e5e5;display:flex;flex-direction:column;position:sticky;top:0;height:100vh;z-index:999;transition:transform .3s cubic-bezier(.4,0,.2,1)}
@media(max-width:1023px){.md-sidebar{position:fixed;left:0}}

.md-icon-btn{width:34px;height:34px;border:none;background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s;border-radius:0}
.md-icon-btn:hover{background:rgba(255,255,255,0.06)}

.md-nav-btn{width:100%;padding:9px 10px;border:none;display:flex;align-items:center;gap:9px;cursor:pointer;transition:all .15s;margin-bottom:1px;font-family:inherit;background:transparent}
.md-nav-btn:hover{background:#fafafa}

.md-dropdown{position:absolute;top:calc(100% + 6px);right:0;background:#0a0a0a;border:1px solid #1c1c1c;box-shadow:0 10px 32px rgba(0,0,0,0.2);overflow:hidden;z-index:200}

.md-grid-btn{padding:10px 6px;border:none;background:transparent;cursor:pointer;text-align:center;display:flex;flex-direction:column;align-items:center;gap:4px;transition:background .1s}
.md-grid-btn:hover{background:rgba(255,255,255,0.04)}

.md-settings-btn{width:100%;padding:9px 14px;border:none;background:transparent;text-align:left;cursor:pointer;font-size:11px;color:#6b6b6b;font-weight:500;display:flex;align-items:center;gap:8px;transition:background .1s;font-family:inherit}
.md-settings-btn:hover{background:rgba(255,255,255,0.04)}

.md-stat-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:12px}
`;